import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Button, Flex, Spin } from "antd"
import { routes } from "router"
import { Icon } from "ui/Icon"
import { useTheme } from "context/AntdThemeContext"
import { BOOKING_MODES } from "pagesV2/pro/Bookings/constants"
import { fetchExperienceById } from "store/experiences"
import { getPublicExperienceOptions } from "store/trips"
import { PreviewAddress } from "pagesV2/pro/Experiences/ListingPreview/PreviewAddress"
import { PreviewMediaListing } from "pagesV2/pro/Experiences/ListingPreview/PreviewMedia"
import PreviewAbout from "pagesV2/pro/Experiences/ListingPreview/PreviewAbout"
import { PreviewMeetingLocation } from "pagesV2/pro/Experiences/ListingPreview/PreviewMeetingLocation"
import { PreviewGuide } from "pagesV2/pro/Experiences/ListingPreview/PreviewGuide"
import UnavailableExperience from "../UnavailableExperience"
import BookingPanel from "./BookingPanel"
import TicketedBookingPanel from "./BookingPanel/TicketedBookingPanel"
import TicketedAbout from "./TicketedDetails/TicketedAbout"
import TicketTypesSection from "./TicketedDetails/TicketTypesSection"
import PriceBar from "./PriceBar"
import ReadMoreText from "../ReadMoreText"
import styles from "./WidgetExperience.module.css"

// Experience page of the new widget: the same details as the old widget's page (shared preview
// blocks) beside the new booking panel. A ticketed experience has its own About (guest limit,
// length, languages) and its ticket types and prices above the shared blocks. Below 992px the
// panel is a sheet that the price bar pinned to the bottom opens.
const WidgetExperience = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { uuid, id } = useParams()
  const { switchTheme } = useTheme()
  const { experienceDetail } = useSelector((state) => state.experiences.experienceById)
  // The store may still hold the previously opened experience until this one loads.
  const experience = experienceDetail?.uuid === id ? experienceDetail : null
  const isTicketed = experience?.experience_type === "ticketed"
  // The experience whose request failed; the page then says so instead of loading forever.
  const [failedId, setFailedId] = useState(null)
  const [isPanelSheetOpen, setIsPanelSheetOpen] = useState(false)

  useEffect(() => {
    if (!id) return
    // Same request as the old widget's page (its third argument is inverted; see plan item FE13).
    dispatch(fetchExperienceById(id, undefined, BOOKING_MODES.embedded)).catch(() => setFailedId(id))
    dispatch(getPublicExperienceOptions(id)).catch(() => {})
  }, [id, dispatch])

  useEffect(() => {
    if (experience?.guide?.adventure_mode) switchTheme(experience.guide.adventure_mode)
  }, [experience?.guide?.adventure_mode, switchTheme])

  const goToListing = () => navigate(routes.widgetListing({ uuid }))

  if (!experience && failedId === id) {
    return (
      <Flex vertical gap={16}>
        <Button
          shape="circle"
          className={styles.backButton}
          aria-label={t("booking_widget.experience.back")}
          onClick={goToListing}
          icon={<Icon type="feather" name="ChevronLeft" width={20} height={20} color="currentColor" />}
        />
        <UnavailableExperience onChooseExperience={goToListing} />
      </Flex>
    )
  }

  if (!experience) {
    return (
      <Flex justify="center" className="p-xl">
        <Spin />
      </Flex>
    )
  }

  return (
    <Flex vertical gap={16}>
      <Button
        shape="circle"
        className={styles.backButton}
        aria-label={t("booking_widget.experience.back")}
        onClick={goToListing}
        icon={<Icon type="feather" name="ChevronLeft" width={20} height={20} color="currentColor" />}
      />
      <Flex vertical gap={4}>
        <h1 className={styles.title}>{experience.title}</h1>
        <PreviewAddress embedded trip={experience} t={t} />
      </Flex>
      <div className={styles.layout}>
        <div className={styles.details}>
          <PreviewMediaListing embedded trip={experience} mobileCarouselClassName={styles.phoneGallery} />
          {isTicketed && (
            <>
              <TicketedAbout experience={experience} />
              <TicketTypesSection ticketTypes={experience.ticket_types} />
            </>
          )}
          <PreviewAbout
            embedded
            hideHeader={isTicketed}
            trip={experience}
            t={t}
            renderDescription={(description) => description && <ReadMoreText text={description} className="text-prewrap" />}
          />
          <PreviewMeetingLocation embedded trip={experience} t={t} />
          <PreviewGuide embedded trip={experience} t={t} />
        </div>
        <aside className={styles.panelColumn}>
          {isTicketed ? (
            <TicketedBookingPanel experience={experience} isSheetOpen={isPanelSheetOpen} onSheetClose={() => setIsPanelSheetOpen(false)} />
          ) : (
            <BookingPanel experience={experience} isSheetOpen={isPanelSheetOpen} onSheetClose={() => setIsPanelSheetOpen(false)} />
          )}
        </aside>
      </div>
      <PriceBar experience={experience} onSelectDate={() => setIsPanelSheetOpen(true)} />
    </Flex>
  )
}

export default WidgetExperience
